/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { parse } from 'papaparse';
import * as moment from 'moment';
import { PlansApi } from 'src/app/app-data/state/PlansApi';
import { Plan } from '@touchplan/common';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

export enum WBSField {
	wbsCode = 'wbsCode',
	touchplanPhase = 'touchplanPhase',
	wbsName = 'wbsName',
	startDate = 'startDate',
	finishDate = 'finishDate',
}

const csvCodeToInternalValue = {
	'wbs code': 'wbsCode',
	'touchplan phase': 'touchplanPhase',
	'wbs name': 'wbsName',
	start: 'startDate',
	finish: 'finishDate',
};

export interface WBSItem {
	wbsCode: number;
	touchplanPhase: string; //Green?
	wbsName: string;
	startDate: string;
	finishDate: string;
}

export enum ParsePhase {
	read = 'read',
	validation = 'validation',
}

@Injectable()
export class WbsSyncService {
	private logger: ILoggerService;

	constructor() {
		this.logger = LoggerServiceFactory.create('WbsSyncService');
	}

	//edits the array in place
	private cleanup(parsed: WBSItem[]) {
		for (let i = parsed.length - 1; i >= 0; i--) {
			//plop out non-green
			if (parsed[i].touchplanPhase !== 'Green') {
				parsed.splice(i, 1);
				break;
			}
			parsed[i].startDate = this.validateDate(parsed[i].startDate, i);
			parsed[i].finishDate = this.validateDate(parsed[i].finishDate, i);
		}
	}

	private validateDate(date: string, lineno: number) {
		const momented = moment(date, 'YYYY-MM-DD', true);
		if (!momented.isValid()) {
			throw this.makeError(
				ParsePhase.validation,
				lineno,
				`date (${date}) invalid. All dates must be in YYYY-MM-DD format.`
			);
		}
		return date;
	}

	private makeError(phase: string, line: number, extra = '', data?) {
		return new Error(`Parse failed during ${phase} on line ${line}. ${extra}`);
	}

	/** accepts a tsv string, returns a parsed and validated array of wbsItems */
	public tsvToJson(tsv: string) {
		const parsed = parse(tsv, {
			header: true,
			skipEmptyLines: true,
			transformHeader: (header: string) => {
				//can fuzzy it more in theory...
				return csvCodeToInternalValue[header.toLowerCase()];
			},
		});
		if (parsed.errors.length) {
			this.logger.error('parse failed', parsed);
			throw this.makeError(ParsePhase.read, -1);
		}
		const parsedData = parsed.data as WBSItem[];
		this.cleanup(parsedData);
		return parsedData;
	}

	/** Saves a list of previously parsed WBSItems into the plan data updating or creating as necessary */
	public saveWbsData(list: WBSItem[], plansApi: PlansApi) {
		duplicatePlanNameSanityCheck(plansApi.data.syncList);

		list.forEach((wbsItem) => {
			const plan = getPlanFromPlanName(wbsItem.wbsName, plansApi.data.syncList);
			const dataToSave = {
				wbsCode: wbsItem.wbsCode,
				targetStartDate: wbsItem.startDate,
				targetFinishDate: wbsItem.finishDate,
			};
			//update
			if (plan) {
				plansApi.localUpdate(plan.$id, dataToSave);
				plansApi.persistData(plan);
			} else {
				dataToSave['name'] = wbsItem.wbsName;
				plansApi.addPlan(dataToSave);
			}
		});
	}
}

//weee inefficiency
function getPlanFromPlanName(planName: string, planList: Map<string, Plan>) {
	let planToReturn: Plan = null;
	planList.forEach((plan) => {
		if (plan.view.name?.toLowerCase() === planName?.toLowerCase()) {
			planToReturn = plan;
		}
	});
	return planToReturn;
}

function duplicatePlanNameSanityCheck(planList: Map<string, Plan>) {
	const cache: any = {};
	planList.forEach((plan) => {
		const name = plan.view.name;
		if (!name || plan.view.deletedAt) {
			return;
		} //skip
		if (cache[name]) {
			throw new Error(`There is more than one plan with the name ${name}. Wbs Sync will not be possible.`);
		}
		cache[name] = true;
	});
}
